import React from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

/**
 * Persistent, non-dismissible alert for a missed statutory Tusla reporting
 * deadline (S.I. 575/2018). Rendered whenever the dashboard-stats
 * `notifiableOverdue` flag is true — on both the dashboard and the incident
 * register, so it's visible wherever a location admin/staff member is
 * working, not just one screen.
 *
 * Deliberately has NO close/dismiss control. A missed statutory deadline
 * isn't a UX annoyance to be dismissed away — it stays on screen until the
 * underlying incident is actually marked reported to Tusla, at which point
 * the backend flag flips to false and the banner naturally disappears (the
 * caller conditionally renders this component; there is no local state here
 * that could go stale or be dismissed independently of the real data).
 */
const NotifiableOverdueBanner: React.FC = () => {
  return (
    <div
      data-testid="notifiable-overdue-banner"
      role="alert"
      className="mb-6 flex items-start gap-3 rounded-md border border-red-300 bg-red-50 px-4 py-3 text-red-800"
    >
      <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-red-600" aria-hidden="true" />
      <div className="text-sm">
        <p className="font-semibold">Tusla report overdue</p>
        <p className="mt-0.5">
          A notifiable incident has passed its statutory reporting deadline. File the Tusla report
          as soon as possible to clear this notice.
        </p>
      </div>
    </div>
  );
};

export default NotifiableOverdueBanner;
